import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatomoTracker } from 'ngx-matomo-client';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { FetchAndApplyPreferredLocale } from '@platform-frontend/transloco';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { SessionSet } from '../session';
import { InitStartup } from '../startup.actions';
import { SetCookielessTrackingRequired } from './account-configuration.actions';
import { AccountConfiguration } from './model/account-configuration';
import { AccountConfigurationStateModel } from './model/account-configuration-state-model';

const DEFAULT_STATE: AccountConfigurationStateModel = {
  accountConfigurationInitialized: false,
  accountConfiguration: null,
  cookielessTrackingRequired: true
};

@State<AccountConfigurationStateModel>({
  name: 'accountConfiguration',
  defaults: DEFAULT_STATE
})
@Injectable()
export class AccountConfigurationState {
  constructor(private http: HttpClient, private matomoTracker: MatomoTracker) {}

  @Selector()
  static accountConfiguration(state: AccountConfigurationStateModel): AccountConfiguration | null {
    return state.accountConfiguration;
  }

  @Selector([AccountConfigurationState.accountConfiguration])
  static isPasswordMissingAndBeneficial(state: AccountConfigurationStateModel, accountConfiguration: AccountConfiguration | null): boolean {
    return (
      accountConfiguration !== null &&
      accountConfiguration.primaryAuthenticationMethod !== 'PASSWORD' &&
      accountConfiguration.primaryAuthenticationMethod !== 'OAUTH'
    );
  }

  @Action(SessionSet)
  sessionSet(ctx: StateContext<AccountConfigurationStateModel>, { isSignedIn }: SessionSet): Observable<unknown> {
    return this.updateAccountConfiguration(ctx, isSignedIn);
  }

  @Action(InitStartup)
  initStartup(ctx: StateContext<AccountConfigurationStateModel>, { isSignedIn }: InitStartup): Observable<unknown> {
    return ctx.getState().accountConfigurationInitialized ? of(null) : this.updateAccountConfiguration(ctx, isSignedIn);
  }

  private updateAccountConfiguration(ctx: StateContext<AccountConfigurationStateModel>, isSignedIn: boolean): Observable<unknown> {
    ctx.patchState({ accountConfigurationInitialized: true });

    if (!isSignedIn) {
      ctx.patchState({ accountConfiguration: null });
      return of(null);
    }

    return combineLatest([
      this.http.get<AccountConfiguration>(PLATFORM_ENVIRONMENT.apiUrl + '/account/configuration').pipe(
        catchError(() => {
          ctx.patchState({ accountConfigurationInitialized: false });
          return of(null);
        }),
        tap(accountConfiguration => ctx.patchState({ accountConfiguration })),
        tap(() => this.applyCookielessTrackingSetup(ctx))
      ),
      ctx.dispatch(new FetchAndApplyPreferredLocale())
    ]);
  }

  @Action(SetCookielessTrackingRequired)
  setCookielessTrackingRequired(ctx: StateContext<AccountConfigurationStateModel>, { cookielessTrackingRequired }: SetCookielessTrackingRequired): void {
    ctx.patchState({ cookielessTrackingRequired });
    this.applyCookielessTrackingSetup(ctx);
  }

  private applyCookielessTrackingSetup(ctx: StateContext<AccountConfigurationStateModel>): void {
    const cookieLessTracking: boolean =
      ctx.getState().cookielessTrackingRequired &&
      (!ctx.getState().accountConfigurationInitialized || (ctx.getState().accountConfiguration?.cookielessTracking ?? false));
    if (cookieLessTracking) {
      this.matomoTracker.forgetUserOptOut();
    } else {
      this.matomoTracker.optUserOut();
    }
  }
}
